import { Divider } from 'antd';
import { useIntl } from 'react-intl';

import { DateUtil } from '@src/packages/utils/dates';
import { useRulesByCountry } from '@src/packages/utils/hooks/useRulesByCountry';
import { formatUserFullName } from '@src/util/format/userFullName';

import type { MarkAttendanceJourneyDetails, MarkAttendanceSelectedUser } from '../types';

import styles from '../MarkAttendanceDrawer.module.less';

type MarkAttendanceJourneyInfoProps = {
  user: MarkAttendanceSelectedUser;
  journeyDetails: MarkAttendanceJourneyDetails | null;
};

const secondsToMs = (seconds: number | null | undefined): number | null =>
  typeof seconds === 'number' ? seconds * 1000 : null;

/** Shared journey detail rows (user, identifier, journey, times, location). */
const MarkAttendanceJourneyInfo = ({ user, journeyDetails }: MarkAttendanceJourneyInfoProps) => {
  const intl = useIntl();
  const { countryRulesForm } = useRulesByCountry();
  const identifierLabel = intl.formatMessage({
    id: countryRulesForm?.identifier?.label ?? 'country.chile.identifier',
  });

  const idCardLabel = user.idCard
    ? countryRulesForm?.identifier?.formatIdentifier(user.idCard) ?? user.idCard
    : '—';

  const formatDateTimeAt = (ms: number | null): string => {
    if (ms == null) {
      return '—';
    }
    return intl.formatMessage(
      { id: 'workJourney.realTime.markAttendance.dateTimeAt' },
      {
        date: DateUtil.toDateString(ms, DateUtil.DEFAULT_DT_DATE_FORMAT),
        time: DateUtil.toDateString(ms, DateUtil.DEFAULT_TIME_WITH_SECONDS_FORMAT),
      },
    );
  };

  const breakStartMs = secondsToMs(journeyDetails?.scheduledBreakStartAtSeconds);
  const breakEndMs = secondsToMs(journeyDetails?.scheduledBreakEndAtSeconds);
  const breakLabel =
    breakStartMs != null && breakEndMs != null
      ? intl.formatMessage(
          { id: 'workJourney.realTime.markAttendance.breakRange' },
          {
            start: DateUtil.toDateString(breakStartMs, DateUtil.DEFAULT_TIME_WITH_SECONDS_FORMAT),
            end: DateUtil.toDateString(breakEndMs, DateUtil.DEFAULT_TIME_WITH_SECONDS_FORMAT),
          },
        )
      : '—';

  return (
    <dl className={styles.detailList}>
      <div className={styles.detailRow}>
        <dt>{intl.formatMessage({ id: 'workJourney.realTime.markAttendance.field.user' })}</dt>
        <dd>{formatUserFullName(user) || user.fullName?.trim() || '—'}</dd>
      </div>
      <Divider className={styles.rowDivider} />
      <div className={styles.detailRow}>
        <dt>{identifierLabel}</dt>
        <dd>{idCardLabel}</dd>
      </div>
      <Divider className={styles.rowDivider} />
      <div className={styles.detailRow}>
        <dt>{intl.formatMessage({ id: 'workJourney.realTime.markAttendance.field.journey' })}</dt>
        <dd>{journeyDetails?.journeyName || '—'}</dd>
      </div>
      <Divider className={styles.rowDivider} />
      <div className={styles.detailRow}>
        <dt>{intl.formatMessage({ id: 'workJourney.realTime.markAttendance.field.starts' })}</dt>
        <dd>{formatDateTimeAt(secondsToMs(journeyDetails?.scheduledStartAtSeconds))}</dd>
      </div>
      <Divider className={styles.rowDivider} />
      <div className={styles.detailRow}>
        <dt>{intl.formatMessage({ id: 'workJourney.realTime.markAttendance.field.break' })}</dt>
        <dd>{breakLabel}</dd>
      </div>
      <Divider className={styles.rowDivider} />
      <div className={styles.detailRow}>
        <dt>{intl.formatMessage({ id: 'workJourney.realTime.markAttendance.field.ends' })}</dt>
        <dd>{formatDateTimeAt(secondsToMs(journeyDetails?.scheduledEndAtSeconds))}</dd>
      </div>
      <Divider className={styles.rowDivider} />
      <div className={styles.detailRow}>
        <dt>{intl.formatMessage({ id: 'workJourney.realTime.markAttendance.field.location' })}</dt>
        <dd>{journeyDetails?.locationLabel || '—'}</dd>
      </div>
    </dl>
  );
};

export default MarkAttendanceJourneyInfo;
